import JSON5 from 'json5';

export interface JSONValidationResult {
  isValid: boolean;
  error?: string;
  parsed?: any;
  line?: number;
  column?: number;
  suggestion?: string;
}

export interface HighlightRange {
  start: number;
  end: number;
  line: number;
  column: number;
}

export function validateAndParseJSON(jsonString: string, allowJSON5: boolean = false): JSONValidationResult {
  if (!jsonString.trim()) {
    return { isValid: false, error: 'Empty JSON input' };
  }

  try {
    const parsed = allowJSON5 ? JSON5.parse(jsonString) : JSON.parse(jsonString);
    return { isValid: true, parsed };
  } catch (error) {
    if (error instanceof Error) {
      const errorMessage = error.message;
      const lineMatch = errorMessage.match(/line (\d+)/i);
      const columnMatch = errorMessage.match(/column (\d+)/i);
      const positionMatch = errorMessage.match(/position (\d+)/i);
      
      let line = lineMatch ? parseInt(lineMatch[1]) : undefined;
      let column = columnMatch ? parseInt(columnMatch[1]) : undefined;
      
      // If we have position but not line/column, calculate them
      if (positionMatch && !line && !column) {
        const position = parseInt(positionMatch[1]);
        const lines = jsonString.substring(0, position).split('\n');
        line = lines.length;
        column = lines[lines.length - 1].length + 1;
      }

      const suggestion = generateSuggestion(errorMessage, jsonString, line, column);

      return {
        isValid: false,
        error: errorMessage,
        line,
        column,
        suggestion
      };
    }
    return { isValid: false, error: 'Invalid JSON format' };
  }
}

export function formatJSON(jsonString: string, allowJSON5: boolean = false): string {
  try {
    const parsed = allowJSON5 ? JSON5.parse(jsonString) : JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    throw new Error('Cannot format invalid JSON');
  }
}

export function findJSONPath(jsonString: string, path: string): HighlightRange | null {
  try {
    JSON.parse(jsonString); // Validate JSON
    const pathParts = parsePath(path);
    
    if (!pathParts.length) {
      // Root selection - highlight entire JSON
      return {
        start: 0,
        end: jsonString.length,
        line: 1,
        column: 1
      };
    }

    return findPathInJSON(jsonString, pathParts);
  } catch (error) {
    return null;
  }
}

function parsePath(path: string): string[] {
  if (!path) return [];
  
  const parts: string[] = [];
  let current = '';
  let inBrackets = false;
  
  for (let i = 0; i < path.length; i++) {
    const char = path[i];
    
    if (char === '[') {
      if (current) {
        parts.push(current);
        current = '';
      }
      inBrackets = true;
    } else if (char === ']') {
      if (inBrackets && current) {
        parts.push(current);
        current = '';
      }
      inBrackets = false;
    } else if (char === '.' && !inBrackets) {
      if (current) {
        parts.push(current);
        current = '';
      }
    } else {
      current += char;
    }
  }
  
  if (current) {
    parts.push(current);
  }
  
  return parts;
}

function findPathInJSON(jsonString: string, pathParts: string[]): HighlightRange | null {
  const lines = jsonString.split('\n');
  let matchedParts = 0;
  let startPos: { line: number; column: number } | null = null;
  let endPos: { line: number; column: number } | null = null;
  
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const trimmedLine = line.trim();
    
    // Skip empty lines and comments
    if (!trimmedLine || trimmedLine.startsWith('//')) continue;
    
    // Look for the current path part
    if (matchedParts < pathParts.length) {
      const currentPart = pathParts[matchedParts];
      
      // Check if this line contains our target key or index
      const keyPattern = new RegExp(`"${escapeRegExp(currentPart)}"\\s*:`);
      const indexPattern = new RegExp(`^\\s*{|\\[`);
      
      if (keyPattern.test(line) || (currentPart.match(/^\d+$/) && indexPattern.test(line))) {
        if (!startPos) {
          startPos = { line: lineIndex + 1, column: 1 };
        }
        matchedParts++;
        
        if (matchedParts === pathParts.length) {
          // Found our target, now find the end
          endPos = findValueEnd(lines, lineIndex, line);
          break;
        }
      }
    }
  }
  
  if (startPos && endPos) {
    const startOffset = getStringOffset(jsonString, startPos.line - 1, startPos.column - 1);
    const endOffset = getStringOffset(jsonString, endPos.line - 1, endPos.column - 1);
    
    return {
      start: startOffset,
      end: endOffset,
      line: startPos.line,
      column: startPos.column
    };
  }
  
  return null;
}

function findValueEnd(lines: string[], startLine: number, _startLineContent: string): { line: number; column: number } {
  // Simple heuristic - if the line ends with a comma or is the last item, use the line end
  for (let i = startLine; i < lines.length; i++) {
    const line = lines[i];
    if (line.includes(':')) {
      // Find the value part after the colon
      const colonIndex = line.indexOf(':');
      const valueStart = colonIndex + 1;
      
      // If value is on same line, find its end
      const restOfLine = line.substring(valueStart).trim();
      if (restOfLine.endsWith(',') || restOfLine.endsWith('}') || restOfLine.endsWith(']')) {
        return { line: i + 1, column: line.length };
      }
    }
    
    // Check for end of object/array
    if (line.trim().endsWith(',') || line.trim().endsWith('}') || line.trim().endsWith(']')) {
      return { line: i + 1, column: line.length };
    }
  }
  
  return { line: startLine + 1, column: lines[startLine].length };
}

function getStringOffset(text: string, line: number, column: number): number {
  const lines = text.split('\n');
  let offset = 0;
  
  for (let i = 0; i < line && i < lines.length; i++) {
    offset += lines[i].length + 1; // +1 for newline character
  }
  
  return offset + column;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function generateSuggestion(errorMessage: string, jsonString: string, line?: number, column?: number): string {
  const lowerError = errorMessage.toLowerCase();
  
  if (lowerError.includes('unexpected token')) {
    if (lowerError.includes("'")) {
      return 'Use double quotes instead of single quotes for strings';
    }
    if (lowerError.includes('unexpected end')) {
      return 'Missing closing bracket or brace';
    }
    return 'Check for missing commas, quotes, or brackets';
  }
  
  if (lowerError.includes('expected')) {
    return 'Check for missing or extra commas';
  }
  
  if (lowerError.includes('duplicate')) {
    return 'Remove duplicate keys in the object';
  }
  
  if (lowerError.includes('trailing comma')) {
    return 'Remove trailing commas (not allowed in JSON)';
  }
  
  return 'Check JSON syntax - ensure proper quotes, commas, and brackets';
}